import { inject, Injectable } from '@angular/core'
import { CacheService } from '../cache/cache.service'
import { Entries, EntriesService } from 'superlikers-microsites-api'

import { User } from '../../interfaces/participant.interface'
import {
  Summary,
  ExternalCircuitKpisSummary,
  ExternalKpi,
  ExternalKpiDetail,
  Kpi,
  KpiDetails,
  UserWithSummary,
  UserWithKpis
} from '../../interfaces/kpis.interface'
import { firstValueFrom, forkJoin, map } from 'rxjs'
import { UsersService } from '../users/users.service'

@Injectable({
  providedIn: 'root'
})
export class KpisService {
  #entriesService = inject(EntriesService)
  #cacheService = inject(CacheService)
  #usersService = inject(UsersService)

  // ============================================
  // CURRENT USER METHODS
  // ============================================

  async getCurrentUserCircuitKpis(circuitSlug: string, user: User, month: string) {
    const kpis = await this.fetchCurrentUserCircuitKpis(circuitSlug, user, month)
    const summary = await this.fetchCurrentUserCircuitSummary(circuitSlug, user, month)

    return { kpis, summary, ...user } as UserWithKpis
  }

  async getCurrentUserGeneralKpis(user: User, month: string) {
    const kpis = await this.fetchCurrentUserGeneralKpis(user, month)
    const summary = await this.fetchCurrentUserGeneralSummary(user, month)

    return { kpis, summary, ...user } as UserWithKpis
  }

  // ============================================
  // USER-LEVEL METHODS - GENERAL KPIs
  // ============================================

  async getAgenciaUsersWithGeneralSummary(agencia: User, month: string): Promise<UserWithSummary[]> {
    const cacheKey = `kpis-summary:${month}:agencia:${agencia.agencia}`
    const cached = this.#cacheService.getItem<UserWithSummary[]>(cacheKey)
    if (cached) return cached

    const users = await this.#usersService.getUsersByAgencia(agencia)
    const summaries = await this.fetchUsersGeneralSummary(users, month)

    const usersWithSummary = users.map(user => ({
      ...user,
      summary: summaries.find(s => s?.userUid === user.uid) || null
    }))

    this.#cacheService.setItem(cacheKey, usersWithSummary)
    return usersWithSummary
  }

  async getEscuderiaUsersWithGeneralSummary(escuderia: User, month: string): Promise<UserWithSummary[]> {
    const cacheKey = `kpis-summary:${month}:escuderia:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<UserWithSummary[]>(cacheKey)
    if (cached) return cached

    const users = await this.#usersService.getUsersByEscuderia(escuderia)
    const summaries = await this.fetchUsersGeneralSummary(users, month)

    const usersWithSummary = users.map(user => ({
      ...user,
      summary: summaries.find(s => s?.userUid === user.uid) || null
    }))

    this.#cacheService.setItem(cacheKey, usersWithSummary)
    return usersWithSummary
  }

  async getAgenciaUsersWithGeneralKpis(agencia: User, month: string): Promise<UserWithKpis[]> {
    const cacheKey = `kpis:${month}:agencia:${agencia.agencia}`
    const cached = this.#cacheService.getItem<UserWithKpis[]>(cacheKey)
    if (cached) return cached

    const users = await this.#usersService.getUsersByAgencia(agencia)
    const summaries = await this.fetchUsersGeneralSummary(users, month)
    const kpis = await this.fetchUsersGeneralKpis(users, month)

    const kpisMap = new Map<string, Kpi[]>()
    kpis.forEach(kpi => {
      const userKpis = kpisMap.get(kpi.userUid) || []
      userKpis.push(kpi)
      kpisMap.set(kpi.userUid, userKpis)
    })

    const usersWithKpis = users.map(user => ({
      ...user,
      summary: summaries.find(s => s?.userUid === user.uid) || null,
      kpis: kpisMap.get(user.uid) || []
    }))

    this.#cacheService.setItem(cacheKey, usersWithKpis)
    return usersWithKpis
  }

  async getEscuderiaUsersWithGeneralKpis(escuderia: User, month: string): Promise<UserWithKpis[]> {
    const cacheKey = `kpis:${month}:escuderia:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<UserWithKpis[]>(cacheKey)
    if (cached) return cached

    const users = await this.#usersService.getUsersByEscuderia(escuderia)
    const summaries = await this.fetchUsersGeneralSummary(users, month)
    const kpis = await this.fetchUsersGeneralKpis(users, month)

    const kpisMap = new Map<string, Kpi[]>()
    kpis.forEach(kpi => {
      const userKpis = kpisMap.get(kpi.userUid) || []
      userKpis.push(kpi)
      kpisMap.set(kpi.userUid, userKpis)
    })

    const usersWithKpis = users.map(user => ({
      ...user,
      summary: summaries.find(s => s?.userUid === user.uid) || null,
      kpis: kpisMap.get(user.uid) || []
    }))

    this.#cacheService.setItem(cacheKey, usersWithKpis)
    return usersWithKpis
  }

  // ============================================
  // AGGREGATE-LEVEL METHODS - GENERAL KPIs
  // ============================================

  async getAgenciaGeneralSummary(agencia: User, month: string): Promise<UserWithSummary> {
    const cacheKey = `kpis-summary:${month}:agencia-entity:${agencia.agencia}`
    const cached = this.#cacheService.getItem<UserWithSummary>(cacheKey)
    if (cached) return cached

    const summary = await this.fetchEntityGeneralSummary(agencia, month)

    const agenciaWithSummary: UserWithSummary = {
      ...agencia,
      summary
    }

    this.#cacheService.setItem(cacheKey, agenciaWithSummary)
    return agenciaWithSummary
  }

  async getEscuderiaGeneralSummary(escuderia: User, month: string): Promise<UserWithSummary> {
    const cacheKey = `kpis-summary:${month}:escuderia-entity:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<UserWithSummary>(cacheKey)
    if (cached) return cached

    const summary = await this.fetchEntityGeneralSummary(escuderia, month)

    const escuderiaWithSummary: UserWithSummary = {
      ...escuderia,
      summary
    }

    this.#cacheService.setItem(cacheKey, escuderiaWithSummary)
    return escuderiaWithSummary
  }

  async getAgenciaGeneralKpis(agencia: User, month: string): Promise<UserWithKpis> {
    const cacheKey = `kpis:${month}:agencia-entity:${agencia.agencia}`
    const cached = this.#cacheService.getItem<UserWithKpis>(cacheKey)
    if (cached) return cached

    const summary = await this.fetchEntityGeneralSummary(agencia, month)
    const kpis = await this.fetchEntityGeneralKpis(agencia, month)

    const agenciaWithKpis: UserWithKpis = {
      ...agencia,
      summary,
      kpis
    }

    this.#cacheService.setItem(cacheKey, agenciaWithKpis)
    return agenciaWithKpis
  }

  async getEscuderiaGeneralKpis(escuderia: User, month: string): Promise<UserWithKpis> {
    const cacheKey = `kpis:${month}:escuderia-entity:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<UserWithKpis>(cacheKey)
    if (cached) return cached

    const summary = await this.fetchEntityGeneralSummary(escuderia, month)
    const kpis = await this.fetchEntityGeneralKpis(escuderia, month)

    const escuderiaWithKpis: UserWithKpis = {
      ...escuderia,
      summary,
      kpis
    }

    this.#cacheService.setItem(cacheKey, escuderiaWithKpis)
    return escuderiaWithKpis
  }

  // ============================================
  // BATCH AGGREGATE METHODS - GENERAL KPIs
  // ============================================

  async getAllEscuderiasGeneralSummary(month: string): Promise<UserWithSummary[]> {
    const escuderias = await this.#usersService.getEscuderias()
    const promises = escuderias.map(escuderia => this.getEscuderiaGeneralSummary(escuderia, month))
    return await Promise.all(promises)
  }

  async getEscuderiaAgenciasGeneralSummary(escuderia: User, month: string): Promise<UserWithSummary[]> {
    const agencias = await this.#usersService.getAgenciasByEscuderia(escuderia)
    const promises = agencias.map(agencia => this.getAgenciaGeneralSummary(agencia, month))
    return await Promise.all(promises)
  }

  async getAllEscuderiasGeneralKpis(month: string): Promise<UserWithKpis[]> {
    const escuderias = await this.#usersService.getEscuderias()
    const promises = escuderias.map(escuderia => this.getEscuderiaGeneralKpis(escuderia, month))
    return await Promise.all(promises)
  }

  async getEscuderiaAgenciasGeneralKpis(escuderia: User, month: string): Promise<UserWithKpis[]> {
    const agencias = await this.#usersService.getAgenciasByEscuderia(escuderia)
    const promises = agencias.map(agencia => this.getAgenciaGeneralKpis(agencia, month))
    return await Promise.all(promises)
  }

  // ============================================
  // USER-LEVEL METHODS - CIRCUIT KPIs
  // ============================================

  async getAgenciaUsersWithSummary(circuitSlug: string, agencia: User, month: string): Promise<UserWithSummary[]> {
    const cacheKey = `${circuitSlug}:summary:${month}:agencia:${agencia.agencia}`
    const cached = this.#cacheService.getItem<UserWithSummary[]>(cacheKey)
    if (cached) return cached

    const users = await this.#usersService.getUsersByAgencia(agencia)
    const summaries = await this.fetchUsersCircuitSummary(circuitSlug, users, month)

    const usersWithSummary = users.map(user => ({
      ...user,
      summary: summaries.find(s => s?.userUid === user.uid) || null
    }))

    this.#cacheService.setItem(cacheKey, usersWithSummary)
    return usersWithSummary
  }

  async getEscuderiaUsersWithSummary(circuitSlug: string, escuderia: User, month: string): Promise<UserWithSummary[]> {
    const cacheKey = `${circuitSlug}:summary:${month}:escuderia:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<UserWithSummary[]>(cacheKey)
    if (cached) return cached

    const users = await this.#usersService.getUsersByEscuderia(escuderia)
    const summaries = await this.fetchUsersCircuitSummary(circuitSlug, users, month)

    const usersWithSummary = users.map(user => ({
      ...user,
      summary: summaries.find(s => s?.userUid === user.uid) || null
    }))

    this.#cacheService.setItem(cacheKey, usersWithSummary)
    return usersWithSummary
  }

  async getAgenciaUsersWithKpis(circuitSlug: string, agencia: User, month: string): Promise<UserWithKpis[]> {
    const cacheKey = `${circuitSlug}:kpis:${month}:agencia:${agencia.agencia}`
    const cached = this.#cacheService.getItem<UserWithKpis[]>(cacheKey)
    if (cached) return cached

    const users = await this.#usersService.getUsersByAgencia(agencia)
    const summaries = await this.fetchUsersCircuitSummary(circuitSlug, users, month)
    const kpis = await this.fetchUsersCircuitKpis(circuitSlug, users, month)

    const kpisMap = new Map<string, Kpi[]>()
    kpis.forEach(kpi => {
      const userKpis = kpisMap.get(kpi.userUid) || []
      userKpis.push(kpi)
      kpisMap.set(kpi.userUid, userKpis)
    })

    const usersWithKpis = users.map(user => ({
      ...user,
      summary: summaries.find(s => s?.userUid === user.uid) || null,
      kpis: kpisMap.get(user.uid) || []
    }))

    this.#cacheService.setItem(cacheKey, usersWithKpis)
    return usersWithKpis
  }

  async getEscuderiaUsersWithKpis(circuitSlug: string, escuderia: User, month: string): Promise<UserWithKpis[]> {
    const cacheKey = `${circuitSlug}:kpis:${month}:escuderia:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<UserWithKpis[]>(cacheKey)
    if (cached) return cached

    const users = await this.#usersService.getUsersByEscuderia(escuderia)
    const summaries = await this.fetchUsersCircuitSummary(circuitSlug, users, month)
    const kpis = await this.fetchUsersCircuitKpis(circuitSlug, users, month)

    const kpisMap = new Map<string, Kpi[]>()
    kpis.forEach(kpi => {
      const userKpis = kpisMap.get(kpi.userUid) || []
      userKpis.push(kpi)
      kpisMap.set(kpi.userUid, userKpis)
    })

    const usersWithKpis = users.map(user => ({
      ...user,
      summary: summaries.find(s => s?.userUid === user.uid) || null,
      kpis: kpisMap.get(user.uid) || []
    }))

    this.#cacheService.setItem(cacheKey, usersWithKpis)
    return usersWithKpis
  }

  // ============================================
  // AGGREGATE-LEVEL METHODS - CIRCUIT KPIs
  // ============================================

  async getAgenciaCircuitSummary(circuitSlug: string, agencia: User, month: string): Promise<UserWithSummary> {
    const cacheKey = `${circuitSlug}:summary:${month}:agencia-entity:${agencia.agencia}`
    const cached = this.#cacheService.getItem<UserWithSummary>(cacheKey)
    if (cached) return cached

    const summary = await this.fetchEntityCircuitSummary(circuitSlug, agencia, month)

    const agenciaWithSummary: UserWithSummary = {
      ...agencia,
      summary
    }

    this.#cacheService.setItem(cacheKey, agenciaWithSummary)
    return agenciaWithSummary
  }

  async getEscuderiaCircuitSummary(circuitSlug: string, escuderia: User, month: string): Promise<UserWithSummary> {
    const cacheKey = `${circuitSlug}:summary:${month}:escuderia-entity:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<UserWithSummary>(cacheKey)
    if (cached) return cached

    const summary = await this.fetchEntityCircuitSummary(circuitSlug, escuderia, month)

    const escuderiaWithSummary: UserWithSummary = {
      ...escuderia,
      summary
    }

    this.#cacheService.setItem(cacheKey, escuderiaWithSummary)
    return escuderiaWithSummary
  }

  async getAgenciaCircuitKpis(circuitSlug: string, agencia: User, month: string): Promise<UserWithKpis> {
    const cacheKey = `${circuitSlug}:kpis:${month}:agencia-entity:${agencia.agencia}`
    const cached = this.#cacheService.getItem<UserWithKpis>(cacheKey)
    if (cached) return cached

    const summary = await this.fetchEntityCircuitSummary(circuitSlug, agencia, month)
    const kpis = await this.fetchEntityCircuitKpis(circuitSlug, agencia, month)

    const agenciaWithKpis: UserWithKpis = {
      ...agencia,
      summary,
      kpis
    }

    this.#cacheService.setItem(cacheKey, agenciaWithKpis)
    return agenciaWithKpis
  }

  async getEscuderiaCircuitKpis(circuitSlug: string, escuderia: User, month: string): Promise<UserWithKpis> {
    const cacheKey = `${circuitSlug}:kpis:${month}:escuderia-entity:${escuderia.escuderia}`
    const cached = this.#cacheService.getItem<UserWithKpis>(cacheKey)
    if (cached) return cached

    const summary = await this.fetchEntityCircuitSummary(circuitSlug, escuderia, month)
    const kpis = await this.fetchEntityCircuitKpis(circuitSlug, escuderia, month)

    const escuderiaWithKpis: UserWithKpis = {
      ...escuderia,
      summary,
      kpis
    }

    this.#cacheService.setItem(cacheKey, escuderiaWithKpis)
    return escuderiaWithKpis
  }

  // ============================================
  // BATCH AGGREGATE METHODS - CIRCUIT KPIs
  // ============================================

  async getAllEscuderiasSummary(circuitSlug: string, month: string): Promise<UserWithSummary[]> {
    const escuderias = await this.#usersService.getEscuderias()
    const promises = escuderias.map(escuderia => this.getEscuderiaCircuitSummary(circuitSlug, escuderia, month))
    return await Promise.all(promises)
  }

  async getEscuderiaAgenciasSummary(circuitSlug: string, escuderia: User, month: string): Promise<UserWithSummary[]> {
    const agencias = await this.#usersService.getAgenciasByEscuderia(escuderia)
    const promises = agencias.map(agencia => this.getAgenciaCircuitSummary(circuitSlug, agencia, month))
    return await Promise.all(promises)
  }

  async getAllEscuderiasKpis(circuitSlug: string, month: string): Promise<UserWithKpis[]> {
    const escuderias = await this.#usersService.getEscuderias()
    const promises = escuderias.map(escuderia => this.getEscuderiaCircuitKpis(circuitSlug, escuderia, month))
    return await Promise.all(promises)
  }

  async getEscuderiaAgenciasKpis(circuitSlug: string, escuderia: User, month: string): Promise<UserWithKpis[]> {
    const agencias = await this.#usersService.getAgenciasByEscuderia(escuderia)
    const promises = agencias.map(agencia => this.getAgenciaCircuitKpis(circuitSlug, agencia, month))
    return await Promise.all(promises)
  }

  // ============================================
  // PRIVATE FETCH METHODS
  // ============================================

  private async fetchCurrentUserCircuitKpis(circuitSlug: string, user: User, month: string) {
    const category = `${circuitSlug}-${month}`
    const detailsCategory = `${circuitSlug}-${month}-details`

    const kpisParams: Record<string, unknown> = { _type: 'External', category }
    const kpisResponse = await firstValueFrom(this.#entriesService.getEntries<ExternalKpi>(kpisParams))

    const detailsParams: Record<string, unknown> = {
      _type: 'External',
      atype: 'kpi_details',
      category: detailsCategory
    }
    const detailsResponse = await firstValueFrom(this.#entriesService.getEntries<ExternalKpiDetail>(detailsParams))

    const results = this.kpisAdapter(user.uid, kpisResponse.data, detailsResponse.data, month, circuitSlug)
    return results
  }

  private async fetchCurrentUserCircuitSummary(circuitSlug: string, user: User, month: string) {
    const category = `summary-${circuitSlug}-${month}`
    const params: Record<string, unknown> = { _type: 'External', category }

    const response = await firstValueFrom(
      this.#entriesService.getEntries<ExternalCircuitKpisSummary>(params).pipe(map(res => res.data.at(0)))
    )

    return this.summaryAdapter(user.uid, month, response?.data, circuitSlug)
  }

  private async fetchCurrentUserGeneralSummary(user: User, month: string) {
    const category = `summary-${month}`
    const params: Record<string, unknown> = { _type: 'External', category }

    const response = await firstValueFrom(
      this.#entriesService.getEntries<ExternalCircuitKpisSummary>(params).pipe(map(res => res.data.at(0)))
    )

    return this.summaryAdapter(user.uid, month, response?.data)
  }

  private async fetchCurrentUserGeneralKpis(user: User, month: string) {
    const category = `kpi_${month}`
    const detailsCategory = `${month}-details`

    const kpisParams: Record<string, unknown> = { _type: 'External', category }
    const kpisResponse = await firstValueFrom(this.#entriesService.getEntries<ExternalKpi>(kpisParams))

    const detailsParams: Record<string, unknown> = {
      _type: 'External',
      atype: 'kpi_details',
      category: detailsCategory
    }
    const detailsResponse = await firstValueFrom(this.#entriesService.getEntries<ExternalKpiDetail>(detailsParams))

    return this.kpisAdapter(user.uid, kpisResponse.data, detailsResponse.data, month)
  }

  private async fetchEntityCircuitSummary(circuitSlug: string, entity: User, month: string): Promise<Summary | null> {
    const category = `summary-${circuitSlug}-${month}`
    const params: Record<string, unknown> = { _type: 'External', category, participant_uid: entity.uid }

    const response = await firstValueFrom(
      this.#entriesService.getEntries<ExternalCircuitKpisSummary>(params).pipe(map(res => res.data.at(0)))
    )

    return this.summaryAdapter(entity.uid, month, response?.data, circuitSlug)
  }

  private async fetchEntityCircuitKpis(circuitSlug: string, entity: User, month: string): Promise<Kpi[]> {
    const category = `${circuitSlug}-${month}`
    const detailsCategory = `${circuitSlug}-${month}-details`

    const kpisParams: Record<string, unknown> = { _type: 'External', category, participant_uid: entity.uid }
    const kpisResponse = await firstValueFrom(this.#entriesService.getEntries<ExternalKpi>(kpisParams))

    const detailsParams: Record<string, unknown> = {
      _type: 'External',
      atype: 'kpi_details',
      category: detailsCategory,
      participant_uid: entity.uid
    }
    const detailsResponse = await firstValueFrom(this.#entriesService.getEntries<ExternalKpiDetail>(detailsParams))

    return this.kpisAdapter(entity.uid, kpisResponse.data, detailsResponse.data, month, circuitSlug)
  }

  private async fetchEntityGeneralSummary(entity: User, month: string): Promise<Summary | null> {
    const category = `summary-${month}`
    const params: Record<string, unknown> = { _type: 'External', category, participant_uid: entity.uid }

    const response = await firstValueFrom(
      this.#entriesService.getEntries<ExternalCircuitKpisSummary>(params).pipe(map(res => res.data.at(0)))
    )

    return this.summaryAdapter(entity.uid, month, response?.data)
  }

  private async fetchEntityGeneralKpis(entity: User, month: string): Promise<Kpi[]> {
    const category = `kpi_${month}`
    const detailsCategory = `${month}-details`

    const kpisParams: Record<string, unknown> = { _type: 'External', category, participant_uid: entity.uid }
    const kpisResponse = await firstValueFrom(this.#entriesService.getEntries<ExternalKpi>(kpisParams))

    const detailsParams: Record<string, unknown> = {
      _type: 'External',
      atype: 'kpi_details',
      category: detailsCategory,
      participant_uid: entity.uid
    }
    const detailsResponse = await firstValueFrom(this.#entriesService.getEntries<ExternalKpiDetail>(detailsParams))

    return this.kpisAdapter(entity.uid, kpisResponse.data, detailsResponse.data, month)
  }

  private async fetchUsersCircuitKpis(circuitSlug: string, users: User[], month: string) {
    const category = `${circuitSlug}-${month}`
    const detailsCategory = `${circuitSlug}-${month}-details`
    const uids = users.map(user => user.uid)

    const kpisParams: Record<string, unknown> = { _type: 'External', category }
    const kpisRequests$ = uids.map(uid =>
      this.#entriesService.getEntries<ExternalKpi>({ ...kpisParams, participant_uid: uid })
    )
    const kpisResponses$ = forkJoin(kpisRequests$)
    const kpisResponses = await firstValueFrom(kpisResponses$)

    const detailsParams: Record<string, unknown> = {
      _type: 'External',
      atype: 'kpi_details',
      category: detailsCategory
    }
    const detailsRequests$ = uids.map(uid =>
      this.#entriesService.getEntries<ExternalKpiDetail>({ ...detailsParams, participant_uid: uid })
    )
    const detailsResponses$ = forkJoin(detailsRequests$)
    const detailsResponses = await firstValueFrom(detailsResponses$)

    const data = uids.flatMap((uid, index) => {
      const kpis = kpisResponses[index].data
      const details = detailsResponses[index].data
      return this.kpisAdapter(uid, kpis, details, month, circuitSlug)
    })

    return data
  }

  private async fetchUsersCircuitSummary(
    circuitSlug: string,
    users: User[],
    month: string
  ): Promise<(Summary | null)[]> {
    const category = `summary-${circuitSlug}-${month}`
    const uids = users.map(user => user.uid)

    const params: Record<string, unknown> = { _type: 'External', category }
    const summariesRequests$ = uids.map(uid =>
      this.#entriesService
        .getEntries<ExternalCircuitKpisSummary>({ ...params, participant_uid: uid })
        .pipe(map(response => response.data.at(0)))
    )
    const summariesResponses$ = forkJoin(summariesRequests$)
    const summariesResponses = await firstValueFrom(summariesResponses$)

    const data = uids.map((uid, index) => {
      const summary = summariesResponses[index]?.data
      return this.summaryAdapter(uid, month, summary, circuitSlug)
    })

    return data
  }

  private async fetchUsersGeneralSummary(users: User[], month: string): Promise<(Summary | null)[]> {
    const category = `summary-${month}`
    const uids = users.map(user => user.uid)

    const params: Record<string, unknown> = { _type: 'External', category }
    const summariesRequests$ = uids.map(uid =>
      this.#entriesService
        .getEntries<ExternalCircuitKpisSummary>({ ...params, participant_uid: uid })
        .pipe(map(response => response.data.at(0)))
    )
    const summariesResponses$ = forkJoin(summariesRequests$)
    const summariesResponses = await firstValueFrom(summariesResponses$)

    const data = uids.map((uid, index) => {
      const summary = summariesResponses[index]?.data
      return this.summaryAdapter(uid, month, summary)
    })

    return data
  }

  private async fetchUsersGeneralKpis(users: User[], month: string) {
    const category = `kpi_${month}`
    const detailsCategory = `${month}-details`
    const uids = users.map(user => user.uid)

    const kpisParams: Record<string, unknown> = { _type: 'External', category }
    const kpisRequests$ = uids.map(uid =>
      this.#entriesService.getEntries<ExternalKpi>({ ...kpisParams, participant_uid: uid })
    )
    const kpisResponses$ = forkJoin(kpisRequests$)
    const kpisResponses = await firstValueFrom(kpisResponses$)

    const detailsParams: Record<string, unknown> = {
      _type: 'External',
      atype: 'kpi_details',
      category: detailsCategory
    }
    const detailsRequests$ = uids.map(uid =>
      this.#entriesService.getEntries<ExternalKpiDetail>({ ...detailsParams, participant_uid: uid })
    )
    const detailsResponses$ = forkJoin(detailsRequests$)
    const detailsResponses = await firstValueFrom(detailsResponses$)

    const data = uids.flatMap((uid, index) => {
      const kpis = kpisResponses[index].data
      const details = detailsResponses[index].data
      return this.kpisAdapter(uid, kpis, details, month)
    })

    return data
  }

  private kpisAdapter(
    uid: string,
    externalKpis: Entries<ExternalKpi>[],
    externalDetails: Entries<ExternalKpiDetail>[],
    month: string,
    circuit?: string
  ) {
    const kpis = externalKpis.map(external => {
      const { data, slug, category } = external

      const percentage = this.transformStringToNumber(data?.percentage) * 100
      const goal = this.transformStringToNumber(data?.goal)
      const progress = this.transformStringToNumber(data?.progress)
      const showDetails = data?.show_details === 'VERDADERO'

      const details = this.getKpiDetails(slug, externalDetails)

      const kpi = {
        name: data?.name || '',
        slug,
        category,
        month,
        userUid: uid,
        goal,
        progress,
        percentage,
        showDetails,
        details
      } as Kpi

      if (circuit) kpi.circuit = circuit

      return kpi
    })

    return kpis
  }

  private summaryAdapter(
    uid: string,
    month: string,
    data: ExternalCircuitKpisSummary | undefined,
    circuit?: string
  ): Summary | null {
    if (!data) return null

    const percentage = this.transformStringToNumber(data?.percentage) * 100
    const goal = this.transformStringToNumber(data?.meta)
    const progress = this.transformStringToNumber(data?.avance)
    const points = this.transformStringToNumber(data?.points)
    const pointsName = data?.points_name

    return {
      circuit,
      month,
      percentage,
      userUid: uid,
      goal,
      progress,
      points,
      pointsName
    }
  }

  private getKpiDetails(kpi: string, externalDetails: Entries<ExternalKpiDetail>[]): KpiDetails[] {
    const map = new Map()

    const kpiExternalDetails = externalDetails.filter(detail => detail.data?.kpi === kpi)

    kpiExternalDetails.forEach(detail => {
      const key = detail.data?.title
      const details = {
        title: detail.data?.title || '',
        description: detail.data?.description || ''
      }

      if (!map.has(key)) map.set(key, details)
    })

    return Array.from(map.values())
  }

  private transformStringToNumber(str?: string | null, decimals = 2) {
    if (!str || isNaN(Number(str))) return 0
    return Number(Number(str).toFixed(decimals))
  }
}
